import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { camerasAPI } from '../utils/api';
import { useSiteStore } from '../stores/siteStore';
import { formatDateTime } from '../utils/formatDateTime';
import toast from 'react-hot-toast';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  TrashIcon,
  VideoCameraIcon,
  ClipboardDocumentIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const PAGE_SIZE = 10;

export default function DeletedCamerasPage() {
  const { selectedSite } = useSiteStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['cameras-deleted', selectedSite?.id, page],
    queryFn: () => camerasAPI.getDeletedCameras(selectedSite?.id, { page, limit: PAGE_SIZE }),
    staleTime: 30_000,
  });

  // Backend returns most-recently-deleted-first, already paginated.
  const paginated = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};
  const total = pagination.total || 0;
  const totalPages = pagination.pages || 1;

  const invalidate = () => {
    queryClient.removeQueries({ queryKey: ['cameras'] });
    queryClient.removeQueries({ queryKey: ['cameras-deleted'] });
    refetch();
  };

  const restoreMutation = useMutation({
    mutationFn: (id) => camerasAPI.restoreCamera(id),
    onSuccess: () => { toast.success('Camera restored — the AI engine will start it shortly'); invalidate(); },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to restore camera'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => camerasAPI.deleteCameraPermanent(id),
    onSuccess: () => { toast.success('Camera permanently deleted'); invalidate(); },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to delete camera'),
  });

  const handleConfirm = () => {
    if (!confirmTarget) return;
    if (confirmTarget.type === 'restore') restoreMutation.mutate(confirmTarget.camera.id);
    else deleteMutation.mutate(confirmTarget.camera.id);
    setConfirmTarget(null);
  };

  const busy = restoreMutation.isPending || deleteMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/cameras')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            title="Back to cameras"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Deleted Cameras</h1>
            <p className="text-gray-500">
              {selectedSite?.name ? `${selectedSite.name} — ` : ''}
              {total > 0 ? `${total} camera${total !== 1 ? 's' : ''} — ` : ''}
              restore a camera to bring it back online
            </p>
          </div>
        </div>
        <button onClick={() => refetch()} className="btn btn-secondary flex items-center gap-2">
          <ArrowPathIcon className={clsxSpin(isFetching)} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* List */}
      <div className="card">
        {isLoading ? (
          <div className="p-12 text-center text-gray-500">Loading…</div>
        ) : paginated.length === 0 ? (
          <div className="p-12 text-center">
            <VideoCameraIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700">No deleted cameras</h3>
            <p className="text-sm text-slate-500 mt-1">Cameras you delete will appear here and can be restored.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="glass-table w-full">
                <thead>
                  <tr>
                    <th className="w-20">Snapshot</th>
                    <th>Camera</th>
                    <th>RTSP URL</th>
                    <th>Location</th>
                    <th>Deleted</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((cam) => (
                    <tr key={cam.id}>
                      {/* Snapshot thumbnail */}
                      <td>
                        {cam.lastSnapshotUrl ? (
                          <img
                            src={cam.lastSnapshotUrl}
                            alt={cam.name}
                            className="w-16 h-10 object-cover rounded border border-slate-200 bg-slate-100 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => setLightboxUrl(cam.lastSnapshotFullUrl || cam.lastSnapshotUrl)}
                            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div
                          className={`w-16 h-10 rounded border border-slate-200 bg-slate-100 items-center justify-center ${cam.lastSnapshotUrl ? 'hidden' : 'flex'}`}
                        >
                          <VideoCameraIcon className="w-5 h-5 text-slate-300" />
                        </div>
                      </td>

                      {/* Camera name + code */}
                      <td>
                        <div className="font-medium text-slate-800">{cam.name}</div>
                        <div className="text-xs text-slate-400">{cam.code || cam.id.slice(0, 8)}</div>
                      </td>

                      {/* RTSP URL with copy */}
                      <td>
                        {cam.rtspUrl ? (
                          <div className="flex items-center gap-1.5 max-w-xs">
                            <span
                              className="text-xs font-mono text-slate-500 truncate"
                              title={cam.rtspUrl}
                            >
                              {cam.rtspUrl}
                            </span>
                            <button
                              onClick={() => { navigator.clipboard.writeText(cam.rtspUrl); toast.success('RTSP URL copied'); }}
                              className="flex-shrink-0 p-1 rounded text-slate-400 hover:text-blue-500 hover:bg-blue-50"
                              title="Copy RTSP URL"
                            >
                              <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">—</span>
                        )}
                      </td>

                      <td className="text-slate-600">{cam.location || '—'}</td>
                      <td className="text-slate-500 text-sm whitespace-nowrap">{cam.deletedAt ? formatDateTime(cam.deletedAt) : '—'}</td>

                      <td>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setConfirmTarget({ camera: cam, type: 'restore' })}
                            disabled={busy}
                            className="btn btn-primary btn-sm flex items-center gap-1 disabled:opacity-50"
                            title="Restore camera"
                          >
                            <ArrowPathIcon className="w-4 h-4" /> Restore
                          </button>
                          <button
                            onClick={() => setConfirmTarget({ camera: cam, type: 'delete' })}
                            disabled={busy}
                            className="btn btn-secondary btn-sm flex items-center gap-1 text-red-600 disabled:opacity-50"
                            title="Delete permanently"
                          >
                            <TrashIcon className="w-4 h-4" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                <p className="text-sm text-slate-500">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1.5 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                    <button
                      key={n}
                      onClick={() => setPage(n)}
                      className={`w-8 h-8 rounded text-sm font-medium ${
                        n === page
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-1.5 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {confirmTarget && (
        <ConfirmationModal
          isOpen={!!confirmTarget}
          onClose={() => setConfirmTarget(null)}
          onConfirm={handleConfirm}
          title={confirmTarget.type === 'restore' ? 'Restore Camera' : 'Delete Camera Permanently'}
          message={confirmTarget.type === 'restore'
            ? `Restore "${confirmTarget.camera.name}"? The AI engine will start processing it again.`
            : `Permanently delete "${confirmTarget.camera.name}"? This cannot be undone.`}
          confirmLabel={confirmTarget.type === 'restore' ? 'Restore' : 'Delete Permanently'}
          variant={confirmTarget.type === 'restore' ? 'info' : 'danger'}
        />
      )}

      {/* Snapshot lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setLightboxUrl(null)}
        >
          <div className="relative max-w-5xl w-full mx-4" onClick={e => e.stopPropagation()}>
            <img
              src={lightboxUrl}
              alt="Snapshot"
              className="w-full max-h-[88vh] object-contain rounded-xl shadow-2xl"
            />
            <button
              onClick={() => setLightboxUrl(null)}
              className="absolute top-3 right-3 p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function clsxSpin(spinning) {
  return spinning ? 'w-4 h-4 animate-spin' : 'w-4 h-4';
}
